// NOTE: duplicate of Ready/apps/workkeys/src/modules/mp-core/components/FormulaSheet/formulaSheetContent.tsx; keep the two in sync.
import React from 'react'
import Fraction from './Fraction'

export type FormulaLine = {
  visual: React.ReactNode
  // Spoken form for lines whose symbols or layout a screen reader would misread.
  spokenText?: string
}

export type FormulaSheetSection = {
  id: string
  heading: string
  lines: FormulaLine[]
}

export const FORMULA_SHEET_TITLE = 'Applied Math Formula Sheet'

export const FORMULA_SHEET_SECTIONS: FormulaSheetSection[] = [
  {
    id: 'distance',
    heading: 'Distance',
    lines: [
      { visual: '1 foot = 12 inches' },
      { visual: '1 yard = 3 feet' },
      { visual: '1 mile = 5,280 feet' },
      {
        visual: '1 mile ≈ 1.61 kilometers',
        spokenText: '1 mile approximately equals 1.61 kilometers',
      },
      { visual: '1 inch = 2.54 centimeters' },
      { visual: '1 foot = 0.3048 meters' },
      { visual: '1 meter = 1,000 millimeters' },
      { visual: '1 meter = 100 centimeters' },
      { visual: '1 kilometer = 1,000 meters' },
    ],
  },
  {
    id: 'area',
    heading: 'Area',
    lines: [
      { visual: '1 square foot = 144 square inches' },
      { visual: '1 square yard = 9 square feet' },
      { visual: '1 acre = 43,560 square feet' },
    ],
  },
  {
    id: 'volume',
    heading: 'Volume',
    lines: [
      { visual: '1 cup = 8 fluid ounces' },
      { visual: '1 quart = 4 cups' },
      { visual: '1 gallon = 4 quarts' },
      { visual: '1 gallon = 231 cubic inches' },
      {
        visual: '1 liter ≈ 0.264 gallons',
        spokenText: '1 liter approximately equals 0.264 gallons',
      },
      { visual: '1 cubic foot = 1,728 cubic inches' },
      { visual: '1 cubic yard = 27 cubic feet' },
      { visual: '1 board foot = 1 inch by 12 inches by 12 inches' },
    ],
  },
  {
    id: 'weight-mass',
    heading: 'Weight/Mass',
    lines: [
      {
        visual: '1 ounce ≈ 28.350 grams',
        spokenText: '1 ounce approximately equals 28.350 grams',
      },
      { visual: '1 pound = 16 ounces' },
      {
        visual: '1 pound ≈ 453.592 grams',
        spokenText: '1 pound approximately equals 453.592 grams',
      },
      { visual: '1 milligram = 0.001 grams' },
      { visual: '1 kilogram = 1,000 grams' },
      {
        visual: '1 kilogram ≈ 2.2 pounds',
        spokenText: '1 kilogram approximately equals 2.2 pounds',
      },
      { visual: '1 ton = 2,000 pounds' },
    ],
  },
  {
    id: 'rectangle',
    heading: 'Rectangle',
    lines: [
      {
        visual: (
          <>
            perimeter = 2(<i>length</i> + <i>width</i>)
          </>
        ),
        spokenText: 'perimeter equals 2 times the quantity length plus width',
      },
      {
        visual: (
          <>
            area = <i>length</i> × <i>width</i>
          </>
        ),
        spokenText: 'area equals length times width',
      },
    ],
  },
  {
    id: 'rectangular-solid',
    heading: 'Rectangular Solid (Box)',
    lines: [
      {
        visual: (
          <>
            volume = <i>length</i> × <i>width</i> × <i>height</i>
          </>
        ),
        spokenText: 'volume equals length times width times height',
      },
    ],
  },
  {
    id: 'cube',
    heading: 'Cube',
    lines: [
      {
        visual: (
          <>
            volume = (<i>length of side</i>)<sup>3</sup>
          </>
        ),
        spokenText: 'volume equals length of side cubed',
      },
    ],
  },
  {
    id: 'triangle',
    heading: 'Triangle',
    lines: [
      {
        visual: 'sum of angles = 180°',
        spokenText: 'sum of angles equals 180 degrees',
      },
      {
        visual: (
          <>
            area = <Fraction numerator='1' denominator='2' />(<i>base</i> ×{' '}
            <i>height</i>)
          </>
        ),
        spokenText: 'area equals one half times base times height',
      },
    ],
  },
  {
    id: 'circle',
    heading: 'Circle',
    lines: [
      {
        visual: 'number of degrees in a circle = 360°',
        spokenText: 'number of degrees in a circle equals 360 degrees',
      },
      {
        visual: (
          <>
            circumference ≈ 3.14 × <i>diameter</i>
          </>
        ),
        spokenText: 'circumference approximately equals 3.14 times diameter',
      },
      {
        visual: (
          <>
            area ≈ 3.14 × (<i>radius</i>)<sup>2</sup>
          </>
        ),
        spokenText: 'area approximately equals 3.14 times radius squared',
      },
    ],
  },
  {
    id: 'cylinder',
    heading: 'Cylinder',
    lines: [
      {
        visual: (
          <>
            volume ≈ 3.14 × (<i>radius</i>)<sup>2</sup> × <i>height</i>
          </>
        ),
        spokenText:
          'volume approximately equals 3.14 times radius squared times height',
      },
    ],
  },
  {
    id: 'cone',
    heading: 'Cone',
    lines: [
      {
        visual: (
          <>
            volume ≈{' '}
            <Fraction
              numerator={
                <>
                  3.14 × (<i>radius</i>)<sup>2</sup> × <i>height</i>
                </>
              }
              denominator='3'
            />
          </>
        ),
        spokenText:
          'volume approximately equals 3.14 times radius squared times height, divided by 3',
      },
    ],
  },
  {
    id: 'sphere',
    heading: 'Sphere (Ball)',
    lines: [
      {
        visual: (
          <>
            volume ≈ <Fraction numerator='4' denominator='3' /> × 3.14 ×
            (<i>radius</i>)<sup>3</sup>
          </>
        ),
        spokenText:
          'volume approximately equals four thirds times 3.14 times radius cubed',
      },
    ],
  },
  {
    id: 'electricity',
    heading: 'Electricity',
    lines: [
      { visual: '1 kilowatt-hour = 1,000 watt-hours' },
      {
        visual: 'amps = watts ÷ volts',
        spokenText: 'amps equals watts divided by volts',
      },
    ],
  },
  {
    id: 'temperature',
    heading: 'Temperature',
    lines: [
      {
        visual: (
          <>
            °C = <Fraction numerator='5' denominator='9' />
            (°F − 32)
          </>
        ),
        spokenText:
          'degrees Celsius equals five ninths times the quantity degrees Fahrenheit minus 32',
      },
      {
        visual: (
          <>
            °F = <Fraction numerator='9' denominator='5' />
            (°C) + 32
          </>
        ),
        spokenText:
          'degrees Fahrenheit equals nine fifths times degrees Celsius, plus 32',
      },
    ],
  },
]
